import React from 'react';
import Select, { components } from 'react-select';
import styled, { useTheme } from 'styled-components';
import { Label } from '../../TokenSwapInputs';
import { ParagraphLargeText } from '../../Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';

const TokenSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  min-width: 105px;
`;

const InputTokenContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 0px;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const TokenInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TokenSymbol = styled(ParagraphLargeText)`
  font-size: 16px;
  color: ${({ theme }) => theme.text.regular};

  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TokenDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 4px 0;
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const Option = ({ data, ...props }) => (
  <components.Option {...props}>
    <OptionContainer>
      <img
        src={data.logoURI || 'https://via.placeholder.com/20'}
        alt={data.symbol}
        style={{ width: '24px', height: '24px' }}
      />
      <TokenInfo>
        <TokenSymbol>{data.label}</TokenSymbol>
      </TokenInfo>
    </OptionContainer>
  </components.Option>
);

const SingleValueContainer = styled.div`
  padding-right: 10px;
`;

const SingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props}>
    <SingleValueContainer>
      <TokenSymbol>{data.label}</TokenSymbol>
    </SingleValueContainer>
  </components.SingleValue>
);

const DisabledValue = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.regular};
`;

const TokenSelector = ({ label, value, disabled, options = [], onChange }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const selectedOption =
    options.find((option) => option.value === value?.value) || options[0];

  const handleChange = (option) => {
    onChange?.(option);
  };

  const theme = useTheme();

  return (
    <TokenSelectorContainer $disabled={disabled}>
      <TokenRow>
        <InputTokenContainer>
          <TokenDisplay>
            <img
              src={selectedOption.logoURI || 'https://via.placeholder.com/20'}
              alt={selectedOption.symbol}
              style={{ width: '24px', height: '24px', flexShrink: 0 }}
            />
            <TokenInfo>
              <Label>{label}</Label>
              {options.length < 2 ? (
                <DisabledValue>{selectedOption?.label}</DisabledValue>
              ) : (
                <Select
                  className='react-select'
                  classNamePrefix='react-select'
                  disabled={disabled}
                  label={label}
                  isSearchable={false}
                  menuIsOpen={options.length > 1 ? undefined : false}
                  styles={{
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: `${theme.background.light}`,
                      margin: 0,
                      padding: '8px',
                      color: state.isSelected
                        ? `${theme.primary.accent}`
                        : `${theme.text.regular}`,
                      '@media (hover: hover)': {
                        '&:hover': {
                          backgroundColor: `${theme.background.secondary}`,
                        },
                      },
                    }),
                    menu: (provided) => ({
                      ...provided,
                      backgroundColor: `${theme.background.light}`,
                      padding: 0,
                      left: isMobile ? -(15 + 8 + 24) : 0,
                      width: isMobile ? 'calc(100vw - 32px)' : '200px',
                      border: 'none',
                      boxShadow: '0 2px 4px rgba(17, 16, 16, 0.4)',
                      borderRadius: '8px',
                    }),
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: theme.background.primary,
                      border: `1px solid ${theme.border.secondary}`,
                      borderRadius: 8,
                      gap: 0,
                      boxShadow: 'none',
                      cursor: options.length > 1 ? 'pointer' : 'default',
                      paddingRight: '6px',
                      paddingLeft: '6px',
                      minHeight: 'unset',
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      padding: 0,
                      margin: 0,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: `${theme.text.secondary}`,
                      padding: '0px',
                      minWidth: '0px',
                      display: options.length > 1 ? 'flex' : 'none',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }),
                    singleValue: (provided) => ({
                      ...provided,
                      margin: '0 4px 0 0',
                    }),
                  }}
                  options={options}
                  value={selectedOption}
                  onChange={handleChange}
                  placeholder='Select Token'
                  components={{ Option, SingleValue }}
                />
              )}
            </TokenInfo>
          </TokenDisplay>
        </InputTokenContainer>
      </TokenRow>
    </TokenSelectorContainer>
  );
};

export default TokenSelector;
